import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import PasswordExpiryGuard from "@/components/PasswordExpiryGuard";
import InactivityGuard from "@/components/InactivityGuard";
import RequireApproval from "@/components/RequireApproval";
import AdminRoute from "@/components/AdminRoute";
import GeoBlockGuard from "@/components/GeoBlockGuard";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";

const Index = lazy(() => import("./pages/Index"));
const HwhcTherapy = lazy(() => import("./pages/HwhcTherapy"));

const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Auth = lazy(() => import("./pages/Auth"));
const Booking = lazy(() => import("./pages/Booking"));
const GovernmentOpportunities = lazy(() => import("./pages/GovernmentOpportunities"));

const Careers = lazy(() => import("./pages/Careers"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const Events = lazy(() => import("./pages/Events"));

const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const AdminApprovals = lazy(() => import("./pages/AdminApprovals"));
const AdminGoogleCalendar = lazy(() => import("./pages/AdminGoogleCalendar"));
const AdminSpaceRequestsPage = lazy(() => import("./pages/AdminSpaceRequests"));
const AdminApplications = lazy(() => import("./pages/AdminApplications"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ReferralDirectory = lazy(() => import("./pages/ReferralDirectory"));
const MyDirectoryProfile = lazy(() => import("./pages/MyDirectoryProfile"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <GeoBlockGuard>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <PasswordExpiryGuard />
            <InactivityGuard />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/hwhc-therapy" element={<HwhcTherapy />} />
                <Route path="/about" element={<Navigate to="/#meet-the-founders" replace />} />
                <Route path="/corey" element={<Navigate to="/#meet-the-founders" replace />} />
                <Route path="/about-us" element={<Navigate to="/" replace />} />
                <Route path="/mya" element={<Navigate to="/hwhc-therapy" replace />} />
                <Route path="/services" element={<Navigate to="/hwhc-therapy" replace />} />
                <Route path="/resources" element={<Blog />} />
                <Route path="/resources/:slug" element={<BlogPost />} />
                <Route path="/blog" element={<Navigate to="/resources" replace />} />
                <Route path="/blog/:slug" element={<Navigate to="/resources" replace />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/contact-us" element={<Booking />} />
                <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
                <Route path="/account-settings" element={<AccountSettings />} />
                <Route path="/for-practitioners" element={<GovernmentOpportunities />} />
                <Route path="/partnerships" element={<Navigate to="/for-practitioners" replace />} />
                <Route path="/government" element={<Navigate to="/for-practitioners" replace />} />
                <Route path="/office" element={<Navigate to="/for-practitioners" replace />} />
                <Route path="/office-rentals" element={<Navigate to="/for-practitioners" replace />} />
                <Route path="/office-usage" element={<Navigate to="/for-practitioners" replace />} />
                <Route path="/office-spaces" element={<Navigate to="/for-practitioners" replace />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/my-bookings" element={<RequireApproval><MyBookings /></RequireApproval>} />
                <Route path="/events" element={<Events />} />
                <Route path="/member-dashboard" element={<Navigate to="/my-bookings" replace />} />
                <Route path="/referral-directory" element={<ReferralDirectory />} />
                <Route path="/my-directory-profile" element={<MyDirectoryProfile />} />
                <Route path="/admin/approvals" element={<AdminRoute><AdminApprovals /></AdminRoute>} />
                <Route path="/admin/google-calendar" element={<AdminRoute><AdminGoogleCalendar /></AdminRoute>} />
                <Route path="/admin/space-requests" element={<AdminRoute><AdminSpaceRequestsPage /></AdminRoute>} />
                <Route path="/admin/applications" element={<AdminRoute><AdminApplications /></AdminRoute>} />
                <Route path="/admin/audit-log" element={<AdminRoute><AdminAuditLog /></AdminRoute>} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfUse />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <CookieConsent />
          </AuthProvider>
        </BrowserRouter>
      </GeoBlockGuard>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
